import type { SanityImageSource } from '@sanity/image-url';
import { createImageUrlBuilder } from '@sanity/image-url';
import { dataset, projectId } from '@/lib/sanity/core/config';
import type { MediaDto } from '@/lib/sanity/dto';

// Plain project coordinates, not the `client` instance: Client Components import
// the helpers below, and passing the client would ship `createClient` with them.
const builder = createImageUrlBuilder({ projectId, dataset });

/** Quality of URLs the browser downloads as they are. */
const IMAGE_QUALITY = 80;

/**
 * Quality of the CDN source next/image re-encodes. Those URLs are an intermediate
 * the optimizer fetches server-side once, never a file the browser downloads, so
 * they stay near-lossless: compressing at 80 here and again in the optimizer costs
 * quality twice for the same delivered bytes.
 */
const OPTIMIZER_SOURCE_QUALITY = 90;

export function urlForImage(source: SanityImageSource) {
    return builder.image(source);
}

export function sanityImageSection(img?: MediaDto, maxWidth = 1920) {
    return img?.asset
        ? builder.image(img).width(maxWidth).quality(IMAGE_QUALITY).auto('format').fit('max').url()
        : undefined;
}

export const sanityImage = {
    og: (img?: MediaDto) =>
        img?.asset ? builder.image(img).width(1200).height(630).fit('max').quality(IMAGE_QUALITY).url() : undefined,

    heroImage: (img?: MediaDto) =>
        img?.asset ? builder.image(img).width(120).quality(OPTIMIZER_SOURCE_QUALITY).url() : undefined,

    heroBackground: (img?: MediaDto) =>
        img?.asset
            ? builder.image(img).width(2200).quality(OPTIMIZER_SOURCE_QUALITY).auto('format').fit('max').url()
            : undefined,

    // <video poster> takes a single URL and cannot be responsive, so keep it lean.
    heroPoster: (img?: MediaDto) =>
        img?.asset ? builder.image(img).width(1600).quality(IMAGE_QUALITY).auto('format').fit('max').url() : undefined,

    // The lightbox shows the picture as large as the window allows, past
    // next/image, so this URL is the file the browser downloads.
    gallery: (img?: MediaDto) =>
        img?.asset ? builder.image(img).width(2200).quality(IMAGE_QUALITY).auto('format').fit('max').url() : undefined,
};

export type SanityImageVariant = keyof typeof sanityImage;
